import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { OrderStatus } from "../../../common/types/OrderStatus";
import { useOrdersModuleApi } from "../../../common/useOrdersModuleApi";
import { defaultOrderDashboardItem, mapToOrderDashboardItem, OrderDashboardItem } from "../OrderDashboardItem";
import { OrderDashboardContext } from "./OrderDashboardContext";
import { useCloudOrderAssetNotes } from "./useCloudOrderAssetNotes";

interface OrderDashboardProviderProps {
    children: React.ReactNode;
}

export const OrderDashboardProvider: React.FC<OrderDashboardProviderProps> = ({
    children
}: OrderDashboardProviderProps) => {
    const { getOrderDetails } = useOrdersModuleApi();

    const { orderId } = useParams<{ orderId: string }>();

    const [orderDashboardItem, setOrderDashboardItem] = useState<OrderDashboardItem>(defaultOrderDashboardItem);
    const { notes, areNotesLoading } = useCloudOrderAssetNotes({ cloudOrder: orderDashboardItem });
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        setIsLoading(true);

        getOrderDetails(orderId)
            .then((response) => {
                const orderDetails = mapToOrderDashboardItem(response.order);

                setOrderDashboardItem(orderDetails);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        setOrderDashboardItem((prevState) => ({ ...prevState, assetNotes: notes ?? [] }));
    }, [notes]);

    const approveDelivery = (): void => {
        setOrderDashboardItem((prevState) => ({ ...prevState, status: OrderStatus.DeliveryApproved }));
    };

    const reject = (): void => {
        setOrderDashboardItem((prevState) => ({ ...prevState, status: OrderStatus.Rejected }));
    };

    const completeRejected = (): void => {
        setOrderDashboardItem((prevState) => ({ ...prevState, status: OrderStatus.DeliveryCompleted }));
    };

    return (
        <OrderDashboardContext.Provider
            value={{
                orderId,
                orderDashboardItem,
                isLoading: isLoading,
                areNotesLoading,
                status: orderDashboardItem.status,
                approveDelivery,
                reject,
                completeRejected
            }}
        >
            {children}
        </OrderDashboardContext.Provider>
    );
};
